import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppId, FCX, ID, ProductLogo, SelectComponent } from '@datassential/platform-ui-lib';
import { AiOutlineSearch } from 'react-icons/ai';
import { toast } from 'react-toastify';
import cn from 'classnames';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import dayjs from 'dayjs';
import CustomScrollbarsVirtualList from '@components/CustomScrollbarsVirtualList/CustomScrollbarsVirtualList';
import { SocialLineChartColumn } from '@components/SocialLineChart/SocialLineChart';
import { useDispatch } from 'react-redux';
import { useChainsWithSocialActive } from '@apiHooks/useChainsWithSocialActive';
import { ChainSocialData, useChainSocialData } from '@apiHooks/useChainSocialData';
import { useChainMonthlyPosts } from '@apiHooks/useChainMonthlyPosts';
import { hasMatches, highlightMatches, prepareForMatching } from '@core/helpers';
import { PermissionLevelEnum } from '@models/PermissionSection';
import { requestTrialByFeature } from '@actions/page';
import PermissionOverlay from '@pages/Food/components/PermissionOverlay/PermissionOverlay';
import BrandPerceptionsSocialChart from './BrandPerceptionsSocialChart';
import BrandPerceptionsPosts from './BrandPerceptionsPosts';
import './HomeBrandPerceptions.scss';

const ITEM_HEIGHT = 40;
const MAX_LIST_HEIGHT = 280;

interface CompetitorOption {
    id: number;
    name: string;
    isActive: boolean;
}

interface CompetitorRowData {
    options: CompetitorOption[];
    selectedCompetitorId: number | null;
    searchQuery: string;
    onSelect: (chain: CompetitorOption) => void;
}

const CompetitorRow = ({ index, style, data }: ListChildComponentProps<CompetitorRowData>) => {
    const chain = data.options[index];
    return (
        <div
            style={style}
            className={cn(
                'HomeBrandPerceptions__dropdown-item',
                !chain.isActive && 'is-disabled',
                data.selectedCompetitorId === chain.id && 'is-selected',
            )}
            onClick={() => data.onSelect(chain)}
            data-testid={`buttonBrandPerceptionsChain_${chain.id}`}
        >
            <span
                dangerouslySetInnerHTML={{
                    __html: highlightMatches(
                        chain.name,
                        data.searchQuery,
                        (m) => `<span class="HomeBrandPerceptions__dropdown-highlight">${m}</span>`,
                    ),
                }}
            />
            {!chain.isActive && (
                <span className="HomeBrandPerceptions__dropdown-no-data">
                    Not available
                </span>
            )}
        </div>
    );
};

export interface BrandPerceptionsSocialPanelProps {
    chainId: ID | null;
    chainName: string;
    chainData: ChainSocialData;
    isLoading?: boolean;
}

const BrandPerceptionsSocialPanel: FCX<BrandPerceptionsSocialPanelProps> = ({
    chainId,
    chainName,
    chainData,
    isLoading = false,
}) => {
    const dispatch = useDispatch();
    const { data: competitorChains } = useChainsWithSocialActive();

    const isLocked = chainData.permissionLevel !== PermissionLevelEnum.Full;

    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCompetitor, setSelectedCompetitor] = useState<CompetitorOption | null>(null);
    const [selectedColumn, setSelectedColumn] = useState<SocialLineChartColumn | null>(null);

    useEffect(() => {
        if (isDropdownOpen && searchInputRef?.current) {
            requestAnimationFrame(() => searchInputRef.current!.focus());
        }
    }, [isDropdownOpen]);

    useEffect(() => {
        setSelectedColumn(null);
        setSelectedCompetitor(null);
    }, [chainId]);

    const { data: competitorData, isLoading: isCompetitorLoading } = useChainSocialData(
        selectedCompetitor?.id ?? null,
    );

    const handleMonthClick = useCallback((column: SocialLineChartColumn | null) => {
        setSelectedColumn(column);
    }, []);

    const selectedMonthStartDate = selectedColumn ? (selectedColumn.id as string) : null;

    const monthlyIds = useMemo<ID[]>(
        () => selectedColumn
            ? [chainId, selectedCompetitor?.id ?? null].filter((x): x is ID => x != null)
            : [],
        [selectedColumn, chainId, selectedCompetitor?.id],
    );

    const { data: monthlyPostsMap, isLoading: isMonthlyLoading } = useChainMonthlyPosts(
        monthlyIds,
        selectedMonthStartDate,
    );

    const monthlyPosts = useMemo(
        () => chainId != null ? (monthlyPostsMap[String(Number(chainId))] ?? []) : [],
        [monthlyPostsMap, chainId],
    );

    const competitorMonthlyPosts = useMemo(
        () => selectedCompetitor ? (monthlyPostsMap[String(Number(selectedCompetitor.id))] ?? []) : [],
        [monthlyPostsMap, selectedCompetitor],
    );

    const displayPosts = useMemo(
        () => selectedColumn ? (isMonthlyLoading ? [] : monthlyPosts) : chainData.posts,
        [selectedColumn, isMonthlyLoading, monthlyPosts, chainData.posts]
    );

    const displayCompetitorPosts = useMemo(() => {
        if (!selectedCompetitor || isCompetitorLoading) return undefined;
        if (selectedColumn) return isMonthlyLoading ? [] : competitorMonthlyPosts;
        return competitorData.posts;
    }, [selectedCompetitor, isCompetitorLoading, selectedColumn, isMonthlyLoading, competitorMonthlyPosts, competitorData.posts]);

    const period = useMemo(
        () => selectedColumn ? dayjs(selectedColumn.id as string).format('MMM YYYY') : undefined,
        [selectedColumn]
    );

    const competitorOptions = useMemo<CompetitorOption[]>(
        () => competitorChains
            .filter(c => c.chainId != null && Number(c.chainId) !== Number(chainId))
            .map(c => ({
                id: Number(c.chainId),
                name: c.chainName ?? '',
                isActive: c.isActive ?? false,
            })),
        [competitorChains, chainId]
    );

    const filteredOptions = useMemo(() => {
        const trimmed = prepareForMatching(searchQuery);
        const matched = trimmed === ''
            ? competitorOptions
            : competitorOptions.filter(c => hasMatches(c.name, searchQuery));

        if (trimmed !== '') {
            matched.sort((a, b) => {
                const aStarts = prepareForMatching(a.name).indexOf(trimmed) === 0;
                const bStarts = prepareForMatching(b.name).indexOf(trimmed) === 0;
                if (aStarts !== bStarts) return aStarts ? -1 : 1;
                return a.name.localeCompare(b.name);
            });
        }

        return matched;
    }, [competitorOptions, searchQuery]);

    const handleDropdownToggle = useCallback((open: boolean) => {
        setIsDropdownOpen(open);
        if (!open) setSearchQuery('');
    }, []);

    const handleSelect = useCallback((chain: CompetitorOption) => {
        if (!chain.isActive) return;
        setSelectedCompetitor(chain);
        setSearchQuery('');
        setIsDropdownOpen(false);
    }, []);

    const handleClear = useCallback(() => {
        setSelectedCompetitor(null);
    }, []);

    const requestTrial = useCallback(() => {
        toast.info('Request has been sent');
        dispatch(requestTrialByFeature('social'));
    }, [dispatch]);

    const thumbnailText = (
        <>
            <AiOutlineSearch className="HomeBrandPerceptions__search-icon"/>
            {selectedCompetitor
                ? selectedCompetitor.name
                : <span
                    style={{ opacity: 0.8 }}>See any competing chain's social listening data for easy comparison</span>
            }
        </>
    );

    return (
        <div className={cn('HomeBrandPerceptions__widget', isLocked && 'is-blocked')}>
            <div className="HomeBrandPerceptions__widget-content">
            <div className="HomeBrandPerceptions__competitor-select">
                <span className="HomeBrandPerceptions__search-label">Competitive Brand</span>
                <SelectComponent
                    isOpened={isDropdownOpen}
                    setIsOpened={handleDropdownToggle}
                    thumbnailText={thumbnailText}
                    isFluidWidth
                    fieldProps={{ modifiers: [] }}
                    dropdownProps={{
                        isOpened: isDropdownOpen,
                        place: 'bottom-start',
                        style: { display: 'block', padding: 6, minWidth: 300 },
                    }}
                >
                    <div className="HomeBrandPerceptions__dropdown-content">
                        <input
                            ref={searchInputRef}
                            type="text"
                            className="HomeBrandPerceptions__dropdown-search"
                            placeholder="Search for a chain..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            data-testid="inputBrandPerceptionsCompetitorSearch"
                        />
                        {selectedCompetitor && (
                            <div className="HomeBrandPerceptions__dropdown-clear" onClick={handleClear}>
                                Clear selection
                            </div>
                        )}
                        <div className="HomeBrandPerceptions__dropdown-options">
                            {filteredOptions.length === 0 && (
                                <div className="HomeBrandPerceptions__dropdown-empty">No chains found</div>
                            )}
                            {filteredOptions.length > 0 && (
                                <div
                                    style={{ height: Math.min(filteredOptions.length * ITEM_HEIGHT, MAX_LIST_HEIGHT) }}>
                                    <AutoSizer>
                                        {({ width, height }) => (
                                            <FixedSizeList
                                                outerElementType={CustomScrollbarsVirtualList}
                                                height={height}
                                                width={width}
                                                itemCount={filteredOptions.length}
                                                itemSize={ITEM_HEIGHT}
                                                itemData={{
                                                    options: filteredOptions,
                                                    selectedCompetitorId: selectedCompetitor?.id ?? null,
                                                    searchQuery,
                                                    onSelect: handleSelect,
                                                }}
                                            >
                                                {CompetitorRow}
                                            </FixedSizeList>
                                        )}
                                    </AutoSizer>
                                </div>
                            )}
                        </div>
                    </div>
                </SelectComponent>
            </div>
            <div className="HomeBrandPerceptions__panels">
                {isLoading ? (
                    <div className="HomeBrandPerceptions__loading">
                        <div className="spinner spinner-blink">
                            <ProductLogo appId={AppId.Snap} type="regular"/>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="HomeBrandPerceptions__chart-panel">
                            <BrandPerceptionsSocialChart
                                chainName={chainName}
                                data={chainData}
                                competitorData={selectedCompetitor && !isCompetitorLoading ? competitorData : null}
                                competitorName={selectedCompetitor?.name}
                                onMonthClick={handleMonthClick}
                                selectedColumnId={selectedColumn?.id ?? null}
                            />
                        </div>
                        <div className="HomeBrandPerceptions__posts-panel">
                            <BrandPerceptionsPosts
                                posts={displayPosts}
                                competitorPosts={displayCompetitorPosts}
                                chainName={chainName}
                                competitorName={selectedCompetitor?.name}
                                hasData={chainData.hasData}
                                period={period}
                                isLoading={selectedColumn !== null && isMonthlyLoading}
                            />
                        </div>
                    </>
                )}
            </div>
            </div>
            {isLocked && (
                <PermissionOverlay
                    onContactUsClick={requestTrial}
                    testId="blockChainSocialPermissionOverlay"
                >
                    <div>Unlock with a subscription to Datassential One.</div>
                </PermissionOverlay>
            )}
        </div>
    );
};

export default BrandPerceptionsSocialPanel;
